import React from "react";
import Axios, { AxiosResponse } from "axios";
import {
  getSessionId,
  init,
  logEvent,
  logException,
  logMessage,
  setEntityId,
  setFeatureSetId,
  setFeatureSwitches,
  setEntityType,
} from "@src/utils/logClient";
import { checkRedirect } from "@src/utils/routes";
import followChannel from "@src/services/ideas/followChannel";
import getFollowingChannels from "@src/services/ideas/getFollowingChannels";
import unfollowChannel from "@src/services/ideas/unfollowChannel";
import cookies from "cookies-js";
import getClasses from "@src/services/ideas/getClasses";
import shareToClassStory from "@src/services/ideas/shareToClassStory";
import applyToBeMentor from "@src/services/applyToBeMentor";
import window from "global/window";
import { FEATURE_SWITCH_ID } from "@src/utils/cookies";
import { APIDomain } from "@src/utils/APIDomain";
import { EntityType } from "@src/utils/getSubdomainURL";
import { logMarketingPageview } from "@src/utils/logMarketingPageview";
import { featureSwitches } from "@src/utils/experiments/constants";

const entityMap = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
  "/": "&#x2F;",
};

const escapeHtml = function (str) {
  // eslint-disable-next-line no-useless-escape
  return String(str).replace(/[&<>"'\/]/g, function (s) {
    return entityMap[s];
  });
};

const isFromUs = function (headers) {
  const header = headers["x-usa"] || headers["X-USA"];
  return header && Number(header) === 1;
};

const isBadParentCodePath = (path) =>
  /^[pP]{1}.+/.test(path) && path !== "proudtoteach" && path !== "privacy" && path !== "playvideo";

const formatAndLogException = async function (msg, url, lineNo, colNo, error: any) {
  let context;
  if (errorShouldBeWarn(msg, error)) {
    if (window.__CD_UNSUPPORTED_BROWSER) msg = `[UNSUPPORTED BROWSER] ${msg}`;

    await logMessage("warn", msg);
  } else {
    if (!url || /classdojo/gi.test(url)) {
      context = "script: " + url + ", lineNo: " + lineNo + ", colNo: " + colNo;
      if (!error) {
        error = {
          message: msg,
          status: "unknown",
          method: "unknown",
          url: url,
          stack: context,
        };
      }
      await logException(error, context);
    }
  }
};

const errorShouldBeWarn = (msg, error) => {
  const msgString = msg.toLowerCase();
  const scriptError = "script error";
  const stack = error && error.stack ? error.stack : "";
  return (
    msgString.indexOf(scriptError) > -1 ||
    msgString.indexOf("classlist") > -1 ||
    stack.indexOf("cookielaw") > -1 ||
    window.__CD_UNSUPPORTED_BROWSER
  );
};

const setOnError = () => {
  window.onerror = formatAndLogException;
  window.addEventListener("unhandledrejection", function (event) {
    if (event.isTrusted) {
      formatAndLogException(event.reason.message, "", "", "", event.reason);
    }
  });
};

type AppData = {
  loadingSession?: boolean;
  type?: EntityType;
  fromUS?: boolean;
  needsDataTransferConsent?: boolean;
  userData?: Partial<{
    type: EntityType;
    firstName: string;
    _id: string;
    lastName: string;
    emailAddress: string;
    [key: string]: unknown;
  }>;
  name?: string;
  followingChannels: Array<any>;
  classes: Array<any>;
  shareStatus: { [key: string]: string };
  featureFlags: any;
  featureFlagsLoaded: boolean;
  mentorApplicationEligibility: any;
  services: Partial<{
    followChannel?: (string: string) => Promise<AxiosResponse<any>>;
    unfollowChannel?: (string: string) => Promise<AxiosResponse<any>>;
    getFollowingChannels?: () => Promise<Record<string, unknown>>;
    logout?: () => void;
    getSession?: () => Promise<void>;
    getClasses?: () => void;
    shareToClassStory?: (classId: string, body: string, image: string) => void;
    getFeatureFlags?: () => void;
    getMentorApplicationEligibility?: () => void;
    applyToBeMentor?: (any) => void;
  }>;
};
type AppDataContextProps = { data: AppData; set: (AppData) => void };

const defaultContextValue: AppDataContextProps = {
  data: {
    loadingSession: true,
    type: undefined,
    fromUS: undefined,
    needsDataTransferConsent: true,
    userData: undefined,
    featureFlags: {},
    followingChannels: [],
    shareStatus: {},
    classes: [],
    services: {},
    featureFlagsLoaded: false,
    mentorApplicationEligibility: false,
  },
  set: () => null,
};

const AppDataContext = React.createContext<AppDataContextProps>(defaultContextValue);

class AppDataContextProvider extends React.Component<Record<string, unknown>, AppDataContextProps> {
  constructor(props: AppDataContextProps) {
    super(props);

    this.setData = this.setData.bind(this);

    defaultContextValue.data.services.getFollowingChannels = async () => {
      try {
        const channels = await getFollowingChannels();
        this.setData({
          followingChannels: channels.status === 200 ? channels.data["_items"] : [],
        });
        return channels;
      } catch (e) {
        console.error("Failed getting followed channels");
      }
    };

    defaultContextValue.data.services.followChannel = async (channel) => {
      const result = await followChannel(channel);
      defaultContextValue.data.services?.getFollowingChannels?.();
      return result;
    };

    defaultContextValue.data.services.unfollowChannel = async (channel) => {
      const result = await unfollowChannel(channel);
      defaultContextValue.data.services?.getFollowingChannels?.();
      return result;
    };

    defaultContextValue.data.services.logout = () => {
      const SESSION = `${APIDomain}/api/session`;
      Axios.delete(SESSION, { withCredentials: true }).then(() => {
        this.setData({
          userData: undefined,
          loadingSession: false,
          type: undefined,
          fromUS: undefined,
          classes: [],
          followingChannels: [],
          shareStatus: {},
        });
      });
    };

    defaultContextValue.data.services.getFeatureFlags = async () => {
      const cookieKey = FEATURE_SWITCH_ID;
      const flagId = cookies.get(cookieKey);
      const locale = cookies.get("dojoDesiredLanguage") || "en-us";

      // I'm unsure when we're setting this on the window
      // we should instead be doing something like `logClient.getSessionId()` here
      const loggingClientSessionId = (window as any).sessionId;
      if (!loggingClientSessionId) {
        setFeatureSwitches({});
        // if we don't have an id, it doesn't make sense to even try to fetch switches
        return;
      }

      const flagGroupKey = "logging";

      let flagUrl = `${APIDomain}/api/featureSwitch?${flagGroupKey}Switches=${featureSwitches}&${flagGroupKey}Id=${loggingClientSessionId}&${flagGroupKey}Locale=${locale}`;
      if (flagId) {
        flagUrl += `&featureSetId=${flagId}`;
      }

      Axios.get(flagUrl)
        .then((result) => {
          const featureSetId = result.data.featureSetId;
          if (featureSetId) {
            setFeatureSetId(featureSetId);
          }
          setFeatureSwitches(result.data[flagGroupKey]);
          this.setData({ featureFlags: result.data[flagGroupKey], featureFlagsLoaded: true });
        })
        .catch((e) => {
          console.error(e);
        });
    };

    defaultContextValue.data.services.getSession = async () => {
      return this.getSession().finally(() => {
        defaultContextValue.data.services?.getFeatureFlags?.();
        if (process.env.GATSBY_BUILD_WEBSITE === "ideas") {
          defaultContextValue.data.services?.getClasses?.();
          defaultContextValue.data.services?.getFollowingChannels?.();
        }
      });
    };

    defaultContextValue.data.services.getClasses = async () => {
      try {
        const result = await getClasses();
        this.setData({
          classes: result,
        });
      } catch (e) {
        console.error("Failed getting classes");
      }
    };
    defaultContextValue.data.services.getMentorApplicationEligibility = async () => {
      try {
        const result = await this.getMentorApplicationEligibility();
        this.setData({
          mentorApplicationEligibility: result,
        });
      } catch (e) {
        console.error("Failed getting mentor application eligibility");
      }
    };

    defaultContextValue.data.services.shareToClassStory = (classId, body, image) => {
      this.setData({
        shareStatus: Object.assign({}, this.state.data.shareStatus, { [classId]: "pending" }),
      });
      shareToClassStory(classId, body, image)
        .then(() => {
          this.setData({
            shareStatus: Object.assign({}, this.state.data.shareStatus, { [classId]: "done" }),
          });
        })
        .catch(() => {
          this.setData({
            shareStatus: Object.assign({}, this.state.data.shareStatus, { [classId]: "error" }),
          });
        });
    };

    defaultContextValue.data.services.applyToBeMentor = (application) => {
      applyToBeMentor(application).then(() => {
        this.setData({
          mentorApplicationEligibility: { applied: true },
        });
      });
    };

    this.state = {
      ...defaultContextValue,
      set: this.setData,
    };
  }

  componentDidMount(): void {
    if (process.env.GATSBY_BUILD_WEBSITE === "www" || process.env.GATSBY_BUILD_WEBSITE === "ideas") {
      checkRedirect(process.env.GATSBY_BUILD_WEBSITE); //redirect to desired locale
    }
    let site = process.env.GATSBY_BUILD_WEBSITE;
    if (process.env.GATSBY_BUILD_WEBSITE === "www") {
      site = "external";
    } else if (process.env.GATSBY_BUILD_WEBSITE === "ideas") {
      site = "external-ideas";
    }

    if (process.env.GATSBY_ENV === "development") {
      init({
        site,
        env: "dev",
        customEndpoint: "/logs",
        productArea: "external",
      });
    } else {
      init({
        site,
        env: "prod",
        customEndpoint: "/logs",
        buildNumber: `${process.env.GATSBY_BUILD_NUMBER}-${process.env.GATSBY_GIT_COMMIT}`,
        productArea: "external",
      });
    }

    setOnError();
    (window.errorCache || []).forEach((errorArgs: [string, string, string, string, any]) => {
      formatAndLogException(...errorArgs);
    });

    if (typeof window !== "undefined") {
      getSessionId().then((sessionId) => ((window as any).sessionId = sessionId));
    }

    this.getNeedsDataTransferConsent();

    this.getSession().finally(() => {
      this.state.data.services?.getFeatureFlags?.();

      if (process.env.GATSBY_BUILD_WEBSITE === "ideas") {
        this.state.data.services?.getFollowingChannels?.();
        this.state.data.services?.getClasses?.();
      }
      logMarketingPageview(window.location);
    });
  }

  getNeedsDataTransferConsent(): Promise<void> {
    return Axios.get(`${APIDomain || ""}/api/needsDataTransferConsent`)
      .then((response) => {
        this.setData({
          needsDataTransferConsent: response.data.needsDataTransferConsent,
        });
      })
      .catch((error) => {
        console.error(
          `Failed getting needsDataTransferConsent value to determine users need to provide consent on signup: ${error}`,
        );
      });
  }

  async getSession(): Promise<void> {
    const SESSION = `${APIDomain || ""}/api/session`;
    return Axios.get(SESSION, { withCredentials: true })
      .then((response) => {
        const data: Partial<AppData> = { fromUS: isFromUs(response.headers) };
        const userData = response.data || {};
        if ("type" in userData) {
          data.type = userData.type;
          if (userData.type === "teacher") {
            data.userData = userData.teacher;
            data.name = escapeHtml(userData.teacher.firstName);
          } else if (userData.type === "parent") {
            data.userData = userData.parent;
            data.name = escapeHtml(userData.parent.firstName);
          } else if (userData.student) {
            data.userData = userData.student;
            data.name = userData.student.firstName ? escapeHtml(userData.student.firstName) : "";
          }
        }
        if (data.userData) data.userData.type = data.type;
        this.setData(data);

        const entity = userData.teacher || userData.parent || userData.student;
        setEntityType(userData);
        setEntityId(entity._id);

        if (window._paq && entity._id) window._paq.push(["setUserId", entity._id]);

        const routeName = window.location.pathname.slice(1).split("/").shift() || "index";
        if (isBadParentCodePath(routeName)) logEvent("user_visit:bad_parent_code_path");
        else logEvent(`user_visit:${routeName}`);
      })
      .catch((error) => {
        setEntityType({});
        // if (error && !isProduction()) throw error;
        this.setData({ fromUS: error.response ? isFromUs(error.response.headers) : false });
      })
      .finally(() => {
        this.setData({ loadingSession: false });
      });
  }

  getMentorApplicationEligibility(): Promise<AxiosResponse["data"]> {
    const SESSION = `${APIDomain || ""}/api/mentorApplicationEligibility`;
    return Axios.get(SESSION, { withCredentials: true }).then((response) => {
      return response.data || {};
    });
  }

  setData(newData: Partial<AppDataContextProps["data"]>): void {
    this.setState((state: AppDataContextProps) => ({
      data: {
        ...state.data,
        ...newData,
      },
    }));
  }

  render(): JSX.Element {
    return <AppDataContext.Provider value={this.state}>{this.props.children}</AppDataContext.Provider>;
  }
}

export { AppDataContext, AppDataContextProvider };
